/**
 * Client-side draft model for the abstract floorplan editor.
 *
 * The editor works on a local draft copy of one floorplan; Save diffs the
 * draft against the last-loaded server state and issues the minimal CRUD
 * sequence (see syncFloorplanDraft.ts). Entities are keyed by a client id
 * (`cid`): for server entities the cid IS the public_id; entities created
 * in the editor get a `tmp-*` cid until Save maps them to real ids.
 */

export type EndpointKind = 'station' | 'zone';

export interface FlowEndpoint {
  kind: EndpointKind;
  cid: string;
}

export interface DraftStation {
  cid: string;
  publicId: string | null;
  name: string;
  description: string;
  station_type: string;
  canvas_x: number;
  canvas_y: number;
  min_staff: number;
  recommended_staff: number;
  staff_ratio_per_students: number | null;
  capacity: number;
  requires_equipment: string[];
  is_required: boolean;
  sort_order: number;
}

export interface DraftZone {
  cid: string;
  publicId: string | null;
  name: string;
  description: string;
  zone_type: string;
  canvas_x: number;
  canvas_y: number;
  canvas_width: number;
  canvas_height: number;
  capacity: number;
  color: string;
}

export interface DraftFlow {
  cid: string;
  publicId: string | null;
  flow_type: string;
  from: FlowEndpoint;
  to: FlowEndpoint;
  description: string;
  is_primary_path: boolean;
  sequence_order: number;
}

export interface FloorplanDraft {
  stations: DraftStation[];
  zones: DraftZone[];
  flows: DraftFlow[];
}

export type EditorTool =
  | { kind: 'select' }
  | { kind: 'pan' }
  | { kind: 'place-station'; stationType: string }
  | { kind: 'draw-zone'; zoneType: string }
  | { kind: 'draw-flow'; flowType: string };

export interface Selection {
  kind: 'station' | 'zone' | 'flow';
  cid: string;
}

export const emptyDraft = (): FloorplanDraft => ({
  stations: [],
  zones: [],
  flows: [],
});
